import { useAuth } from '../auth/use-auth'
import { FOCUS_RING_CLASS } from '../components/focus-ring'

/**
 * DevJwtPanel - collapsible disclosure that shows the current id_token
 * claims, for debugging auth (role/tenant mismatch, token expiry, audience
 * or issuer drift during the M1->M2 issuer swap).
 *
 * Rendered inline inside `AppFooter`, so expanding it grows the footer's
 * height rather than floating over the page. Because `AppFooter` mounts on
 * every screen (sign-in, access-denied, error, 404 and the gated AppShell
 * pages alike), the panel is available everywhere.
 *
 * Gating:
 *   - Non-prod only. Reads `VITE_ENV` in-body and renders nothing when it is
 *     `prod`, so the panel is absent from the production bundle/render (the
 *     footer then shows just the build strip). Read in-body rather than at
 *     module scope so it stays stubbable with `vi.stubEnv` in tests;
 *     module-scope `import.meta.env.*` is folded at build time - see
 *     `dev/flags.ts`.
 *   - When there is no signed-in account (e.g. the sign-in screen) it shows
 *     a "No active session" note rather than hiding, so the control is
 *     present and consistent across all screens.
 *
 * Content: the full raw `account.idTokenClaims` bag, pretty-printed, plus a
 * small humanised view of the epoch claims (`exp`/`iat`/`nbf`).
 *
 * Accessibility: native `<details>`/`<summary>` (collapsed by default) - the
 * same disclosure pattern used by `Sm2MetadataSection`. Keyboard + ARIA come
 * for free; the summary carries the shared focus ring.
 */
export function DevJwtPanel(): JSX.Element | null {
  const env = import.meta.env.VITE_ENV ?? 'dev'
  const { account } = useAuth()

  if (env === 'prod') return null

  const claims = account ? ((account.idTokenClaims ?? {}) as Record<string, unknown>) : null
  const epochLines = claims
    ? EPOCH_CLAIMS.map((key) => ({ key, line: formatEpoch(key, claims[key]) })).filter(
        (e): e is { key: (typeof EPOCH_CLAIMS)[number]; line: string } => e.line !== null,
      )
    : []

  return (
    <details data-testid="dev-jwt-panel" className="mt-murmuration-2">
      <summary
        className={`cursor-pointer select-none ${FOCUS_RING_CLASS} focus-visible:ring-offset-1`}
      >
        JWT claims (dev){account ? '' : ' - no session'}
      </summary>
      {claims ? (
        <>
          {epochLines.length > 0 && (
            <dl className="mt-murmuration-2 grid grid-cols-[auto,1fr] gap-x-murmuration-3 gap-y-murmuration-1">
              {epochLines.map(({ key, line }) => (
                <div key={key} className="contents">
                  <dt className="font-mono">{key}</dt>
                  <dd className="font-mono">{line}</dd>
                </div>
              ))}
            </dl>
          )}
          <pre className="mt-murmuration-2 max-h-[60vh] max-w-[min(92vw,40rem)] overflow-auto rounded bg-bg-surface-2 px-murmuration-3 py-murmuration-2 font-mono text-murmuration-code text-text-primary">
            {JSON.stringify(claims, null, 2)}
          </pre>
        </>
      ) : (
        <p className="mt-murmuration-2">No active session - sign in to inspect the token.</p>
      )}
    </details>
  )
}

/** Standard JWT claims carrying epoch-second timestamps. */
const EPOCH_CLAIMS = ['exp', 'iat', 'nbf'] as const

/**
 * Render an epoch-second claim as "<ISO> (<relative>)", or null when the
 * value is missing / not a finite number. `exp` gets an expires-in / expired
 * relative; `iat`/`nbf` get an issued-/valid-from relative.
 */
function formatEpoch(key: string, value: unknown): string | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null
  const iso = new Date(value * 1000).toISOString()
  const deltaMin = Math.round((value * 1000 - Date.now()) / 60000)
  let relative: string
  if (key === 'exp') {
    relative = deltaMin >= 0 ? `expires in ${deltaMin} min` : `expired ${-deltaMin} min ago`
  } else {
    relative = deltaMin <= 0 ? `${-deltaMin} min ago` : `in ${deltaMin} min`
  }
  return `${iso} (${relative})`
}
